import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  Building2,
  Users,
  DollarSign,
  TrendingUp,
  Plus,
  Loader2,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Copy,
  Check,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { AdminUser } from '@/lib/authService'

interface Church {
  id: string
  name: string
  church_code: string | null
  email: string
  phone: string
  website: string
  created_at: string
  subscription?: {
    plan: string
    status: string
    monthly_price: number
  }
  stats?: {
    total_members: number
    active_members: number
    total_contributions_amount: number
  }
  onboarding?: {
    status: string
  }
}

interface PlatformStats {
  totalChurches: number
  activeChurches: number
  totalMembers: number
  monthlyRevenue: number
  pendingOnboarding: number
}

interface PlatformDashboardProps {
  user: AdminUser | null
}

export default function PlatformDashboard({ user }: PlatformDashboardProps) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<PlatformStats>({
    totalChurches: 0,
    activeChurches: 0,
    totalMembers: 0,
    monthlyRevenue: 0,
    pendingOnboarding: 0,
  })
  const [churches, setChurches] = useState<Church[]>([])
  const [copiedCode, setCopiedCode] = useState<string | null>(null)

  // Check if user is super admin
  if (user?.adminLevel !== 'super_admin') {
    return (
      <div className="flex items-center justify-center h-screen">
        <Card className="w-96">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Access Denied
            </CardTitle>
            <CardDescription>
              You do not have permission to access the platform admin dashboard.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} variant="outline" className="w-full">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  useEffect(() => {
    console.log('🚀 [Platform Dashboard] Component mounted')
    console.log('👤 [Platform Dashboard] Current user:', {
      email: user?.email,
      adminLevel: user?.adminLevel,
      churchId: user?.churchId,
      firstName: user?.firstName,
      lastName: user?.lastName,
    })
    loadPlatformData()
  }, [])

  async function loadPlatformData() {
    try {
      setLoading(true)

      console.log('🔍 [Platform Dashboard] Starting to load platform data...')
      console.log('🔑 [Platform Dashboard] Current user:', user)

      // First, check if we can connect to Supabase
      const { data: testData, error: testError } = await supabase
        .from('churches')
        .select('id, name, church_code')
        .limit(1)

      console.log('🧪 [Platform Dashboard] Test query result:', {
        data: testData,
        error: testError
      })

      if (testError) {
        console.error('❌ [Platform Dashboard] Test query failed:', testError)
        alert(`Database Error: ${testError.message}\n\nDetails: ${testError.details || 'No details'}\n\nHint: ${testError.hint || 'No hint'}`)
        throw testError
      }

      // Load all churches with their subscriptions and stats
      const { data: churchesData, error: churchesError } = await supabase
        .from('churches')
        .select(`
          *,
          church_subscriptions (
            plan,
            status,
            monthly_price
          ),
          church_onboarding (
            status
          )
        `)
        .order('created_at', { ascending: false })

      console.log('📊 [Platform Dashboard] Churches query result:', {
        data: churchesData,
        error: churchesError,
        count: churchesData?.length || 0
      })

      if (churchesError) {
        console.error('❌ [Platform Dashboard] Error loading churches:', churchesError)
        alert(`Error loading churches: ${churchesError.message}\n\nDetails: ${churchesError.details || 'No details'}\n\nThis usually means:\n1. The platform admin tables don't exist\n2. Row Level Security (RLS) is blocking access\n3. You're not logged in as a super_admin`)
        throw churchesError
      }

      // Get current month stats for each church
      const currentMonth = new Date().getMonth() + 1
      const currentYear = new Date().getFullYear()

      console.log('📅 [Platform Dashboard] Current period:', { currentMonth, currentYear })

      const churchesWithStats = await Promise.all(
        (churchesData || []).map(async (church) => {
          console.log(`📍 [Platform Dashboard] Loading stats for church: ${church.name} (${church.id})`)

          const { data: statsData, error: statsError } = await supabase
            .from('church_usage_stats')
            .select('*')
            .eq('church_id', church.id)
            .eq('stat_month', currentMonth)
            .eq('stat_year', currentYear)
            .single()

          console.log(`  Stats for ${church.name}:`, { statsData, statsError })

          return {
            ...church,
            subscription: church.church_subscriptions?.[0],
            onboarding: church.church_onboarding?.[0],
            stats: statsData || {
              total_members: 0,
              active_members: 0,
              total_contributions_amount: 0,
            },
          }
        })
      )

      console.log('✅ [Platform Dashboard] Churches with stats:', churchesWithStats)

      setChurches(churchesWithStats)

      // Calculate platform stats
      const totalChurches = churchesWithStats.length
      const activeChurches = churchesWithStats.filter(
        (c) => c.subscription?.status === 'active' || c.subscription?.status === 'trial'
      ).length
      const totalMembers = churchesWithStats.reduce(
        (sum, c) => sum + (c.stats?.total_members || 0),
        0
      )
      const monthlyRevenue = churchesWithStats.reduce(
        (sum, c) => sum + (c.subscription?.monthly_price || 0),
        0
      )
      const pendingOnboarding = churchesWithStats.filter(
        (c) => c.onboarding?.status === 'in_progress' || c.onboarding?.status === 'not_started'
      ).length

      console.log('📈 [Platform Dashboard] Calculated stats:', {
        totalChurches,
        activeChurches,
        totalMembers,
        monthlyRevenue,
        pendingOnboarding,
      })

      setStats({
        totalChurches,
        activeChurches,
        totalMembers,
        monthlyRevenue,
        pendingOnboarding,
      })

      console.log('✅ [Platform Dashboard] Data loading complete!')
    } catch (error) {
      console.error('❌ [Platform Dashboard] Error loading platform data:', error)
    } finally {
      setLoading(false)
    }
  }

  function getSubscriptionStatusBadge(status: string) {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Active</Badge>
      case 'trial':
        return <Badge className="bg-blue-500"><Clock className="h-3 w-3 mr-1" />Trial</Badge>
      case 'past_due':
        return <Badge className="bg-yellow-500"><AlertCircle className="h-3 w-3 mr-1" />Past Due</Badge>
      case 'cancelled':
        return <Badge className="bg-gray-500"><XCircle className="h-3 w-3 mr-1" />Cancelled</Badge>
      case 'suspended':
        return <Badge className="bg-red-500"><XCircle className="h-3 w-3 mr-1" />Suspended</Badge>
      default:
        return <Badge variant="outline">No Subscription</Badge>
    }
  }

  function getOnboardingStatusBadge(status: string) {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Complete</Badge>
      case 'in_progress':
        return <Badge className="bg-blue-500"><Clock className="h-3 w-3 mr-1" />In Progress</Badge>
      case 'not_started':
        return <Badge variant="outline"><Clock className="h-3 w-3 mr-1" />Not Started</Badge>
      default:
        return <Badge variant="outline">Unknown</Badge>
    }
  }

  function copyChurchCode(code: string) {
    navigator.clipboard.writeText(code)
    setCopiedCode(code)
    setTimeout(() => setCopiedCode(null), 2000)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading platform data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Platform Administration</h1>
          <p className="text-muted-foreground">Manage all churches and platform settings</p>
        </div>
        <Button onClick={() => navigate('/platform/churches/new')}>
          <Plus className="h-4 w-4 mr-2" />
          Add Church
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Churches</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalChurches}</div>
            <p className="text-xs text-muted-foreground">
              {stats.activeChurches} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Members</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalMembers.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Across all churches
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${stats.monthlyRevenue.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Recurring revenue
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Onboarding</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pendingOnboarding}</div>
            <p className="text-xs text-muted-foreground">
              Needs attention
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Growth Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">+12%</div>
            <p className="text-xs text-muted-foreground">
              vs last month
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Churches Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Churches</CardTitle>
          <CardDescription>Manage and monitor all churches on the platform</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Church Name</TableHead>
                <TableHead>Church Code</TableHead>
                <TableHead>Subscription</TableHead>
                <TableHead>Onboarding</TableHead>
                <TableHead>Members</TableHead>
                <TableHead>Monthly Revenue</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {churches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No churches found. Click "Add Church" to get started.
                  </TableCell>
                </TableRow>
              ) : (
                churches.map((church) => (
                  <TableRow key={church.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{church.name}</div>
                        <div className="text-sm text-muted-foreground">{church.email}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {church.church_code ? (
                        <div className="flex items-center gap-2">
                          <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
                            {church.church_code}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => copyChurchCode(church.church_code!)}
                          >
                            {copiedCode === church.church_code ? (
                              <Check className="h-3 w-3 text-green-500" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">Not generated</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {getSubscriptionStatusBadge(church.subscription?.status || 'none')}
                      {church.subscription?.plan && (
                        <div className="text-xs text-muted-foreground mt-1 capitalize">
                          {church.subscription.plan}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {getOnboardingStatusBadge(church.onboarding?.status || 'not_started')}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{church.stats?.total_members || 0}</div>
                      <div className="text-xs text-muted-foreground">
                        {church.stats?.active_members || 0} active
                      </div>
                    </TableCell>
                    <TableCell>
                      ${church.subscription?.monthly_price?.toFixed(2) || '0.00'}
                    </TableCell>
                    <TableCell>
                      {new Date(church.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/platform/churches/${church.id}`)}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}
